import React, { useEffect } from 'react';
import {
  Button,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Space,
  Spin,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoney } from '@/utils/format';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { EpcWbsLibraryPlugModal } from './EpcWbsLibraryPlugModal';

const { Text } = Typography;

export interface EpcEstimateBoqLineDrawerProps {
  open: boolean;
  onClose: () => void;
  estimateId: string;
  revisionId: string;
  lineId: string | null;
  readOnly?: boolean;
  onAddCostLine?: (boqLineId: string) => void;
  onOpenCostDetail?: (line: Record<string, unknown>) => void;
  onOpenMaterials?: (lineId: string) => void;
}

export const EpcEstimateBoqLineDrawer: React.FC<EpcEstimateBoqLineDrawerProps> = ({
  open,
  onClose,
  estimateId,
  revisionId,
  lineId,
  readOnly,
  onAddCostLine,
  onOpenCostDetail,
  onOpenMaterials,
}) => {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [tab, setTab] = React.useState('summary');
  const [plugOpen, setPlugOpen] = React.useState(false);

  const detailQ = useQuery({
    queryKey: ['epc-est-boq-detail', estimateId, revisionId, lineId],
    queryFn: () => epcEstimationService.getClientBoqLineDetail(estimateId, revisionId, lineId!),
    enabled: open && !!lineId,
  });

  const boqLine = detailQ.data?.boq_line;
  const linkedCosts = detailQ.data?.linked_cost_lines ?? [];
  const costTotal = detailQ.data?.cost_total ?? 0;
  const sellAmount = Number(boqLine?.sell_amount ?? detailQ.data?.sell_amount ?? 0);
  const marginPct = detailQ.data?.margin_pct;

  useEffect(() => {
    if (open && boqLine) {
      form.setFieldsValue({
        itemNumber: boqLine.item_number,
        description: boqLine.description,
        quantity: boqLine.quantity,
        unitRate: boqLine.unit_rate,
      });
      setTab('summary');
    }
  }, [open, boqLine, form]);

  const patchM = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcEstimationService.patchClientBoq(estimateId, lineId!, revisionId, values),
    onSuccess: () => {
      message.success('BOQ line updated');
      qc.invalidateQueries({ queryKey: ['epc-est-boq', estimateId, revisionId] });
      qc.invalidateQueries({ queryKey: ['epc-est-boq-detail', estimateId, revisionId, lineId] });
      qc.invalidateQueries({ queryKey: ['epc-est-explorer', estimateId, revisionId] });
      qc.invalidateQueries({ queryKey: ['epc-est-boq-coverage', estimateId, revisionId] });
    },
    onError: (e: Error & { response?: { data?: { message?: string } } }) =>
      message.error(e?.response?.data?.message || e?.message || 'Update failed'),
  });

  const title = boqLine?.description || boqLine?.item_number || 'BOQ line';

  return (
    <Drawer
      title={title}
      open={open && !!lineId}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        !readOnly && lineId ? (
          <Space>
            <Button size="small" onClick={() => setPlugOpen(true)}>
              Plug library package
            </Button>
            {onAddCostLine ? (
              <Button type="primary" size="small" onClick={() => onAddCostLine(lineId)}>
                Add cost line
              </Button>
            ) : null}
          </Space>
        ) : null
      }
    >
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'summary',
            label: 'Summary',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Spin spinning={detailQ.isLoading}>
                  <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Item">{boqLine?.item_number || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Qty">{boqLine?.quantity ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Schedule">
                    {formatMoney(boqLine?.amount, { display: 'plain', maximumFractionDigits: 0 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cost total">
                    {formatMoney(costTotal, { display: 'plain', maximumFractionDigits: 0 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sell">
                    {formatMoney(sellAmount, { display: 'plain', maximumFractionDigits: 0 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Margin">
                    {marginPct != null ? `${marginPct}%` : '—'}
                  </Descriptions.Item>
                  </Descriptions>
                </Spin>
                {!readOnly && lineId && onAddCostLine ? (
                  <Button type="primary" block onClick={() => onAddCostLine(lineId)}>
                    Add cost line
                  </Button>
                ) : null}
              </Space>
            ),
          },
          {
            key: 'costs',
            label: `Linked costs (${linkedCosts.length})`,
            children:
              linkedCosts.length === 0 ? (
                <Empty description="No linked costs yet">
                  {!readOnly && lineId && onAddCostLine ? (
                    <Button type="primary" onClick={() => onAddCostLine(lineId)}>
                      Add cost line
                    </Button>
                  ) : null}
                </Empty>
              ) : (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={linkedCosts}
                  pagination={linkedCosts.length > 20 ? { pageSize: 20 } : false}
                  onRow={(row) => ({
                    onClick: () => onOpenCostDetail?.(row),
                    style: { cursor: onOpenCostDetail ? 'pointer' : undefined },
                  })}
                  columns={[
                    { title: 'Description', dataIndex: 'description', ellipsis: true },
                    { title: 'Kind', dataIndex: 'resource_kind', width: 90 },
                    {
                      title: 'Total',
                      dataIndex: 'total_amount',
                      width: 100,
                      render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
                    },
                    {
                      title: '',
                      width: 72,
                      render: (_: unknown, row: { id: string; resource_kind?: string }) =>
                        row.resource_kind === 'MATERIAL' && onOpenMaterials ? (
                          <Button
                            size="small"
                            type="link"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenMaterials(row.id);
                            }}
                          >
                            Build-up
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              ),
          },
          {
            key: 'edit',
            label: 'Edit',
            disabled: readOnly,
            children: readOnly ? (
              <Text type="secondary">Approved revision — BOQ is read-only.</Text>
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={(v) => patchM.mutate(v)}
              >
                <Form.Item name="itemNumber" label="Item #">
                  <Input />
                </Form.Item>
                <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name="quantity" label="Quantity">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="unitRate" label="Unit rate">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={patchM.isPending}>
                  Save changes
                </Button>
              </Form>
            ),
          },
        ]}
      />
      {lineId && (
        <EpcWbsLibraryPlugModal
          open={plugOpen}
          onClose={() => setPlugOpen(false)}
          estimateId={estimateId}
          revisionId={revisionId}
          clientBoqLineId={lineId}
          boqLineLabel={title}
        />
      )}
    </Drawer>
  );
};
